/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable react-refresh/only-export-components */
import { env } from "cloudflare:workers";
import type React from "react";
import { Suspense, lazy, useCallback, useMemo, useState } from "react";
import type { ShouldRevalidateFunction } from "react-router";
import {
  Outlet,
  useLoaderData,
  useLocation,
  useMatches,
  useRouteLoaderData,
} from "react-router";

import {
  BUILDER_FETCH_FAILED,
  CRITICAL_CHROME_FANOUT_INDICES,
  DEGRADED_CHROME_MARK,
  classifyChromeFanout,
  isDegradedChrome,
  isLayoutDocumentRequest,
} from "./layout-chrome.server";
import { SWR } from "~/.server/caching.server";
import {
  getSession,
  isAuthenticatedUser,
  optionalAuthenticatedUser,
} from "~/.server/sessions.server";
import { getTimingContext } from "~/.server/timing.server";
import { fetchAlertBarContent } from "~/hec/.server/alert-bar-content.server";
import { getMagicLinkConfig } from "~/hec/.server/bloomreach-transactional.server";
import { cachedCategoryNav } from "~/hec/.server/category-nav-swr.server";
import { getLmnTileContent } from "~/hec/.server/lmn-content.server";
import { getTelehealthTilePricing } from "~/hec/.server/telehealth-pricing.server";
import {
  getAfidFromRequest,
  isLmnNavEnabled,
  resolveLmnAllowed,
} from "~/hec/helpers/tpa.server";

import { CHROME_GROUP_TAG } from "@hec/builder-tags";

import {
  AccountDrawerRetryOnHash,
  DrawerRendered,
} from "~/components/account-drawer/account-drawer-retry";
import { parseDrawerHash } from "~/components/account-drawer/drawer-hash";
import { BackToTop } from "~/components/back-to-top";
import { BuilderFooter } from "~/components/builder-footer/builder-footer";
import { DeferredHydrationProvider } from "~/components/deferred-hydration";
import { HecHeader } from "~/components/hec-header";
import { remapTpaBannerHtml } from "~/components/hec-header/tpa-banner-html";
import { OverlayBoundary } from "~/components/overlay-boundary";
import type { OverlayFailure } from "~/components/overlay-retry";
import { MediaScreenProvider } from "~/contexts/madia-screen";
import {
  BUILDER_PAGE_TREE_CACHE_CONTROL,
  swrFetchOneEntry,
} from "~/hec/.server/builder-cache";
import { getBuilderConfig } from "~/hec/.server/config";
import { layoutCacheTags } from "~/hec/.server/layout-cache-tags";
import { TpaLearningCenterConsentGate } from "~/hec/builder/blocks/components/tpa-learning-center-consent/gate";
import { ModelMap } from "~/hec/builder/model-map";
import {
  isEnabled,
  resolveDeferHydrationMode,
} from "~/hec/config/integration-flags";
import { AuthProvider } from "~/hec/context/auth-context";
import { useOverlaysReady } from "~/hec/hooks/useOverlaysReady";
import { isPerksMutationSubmit } from "~/hec/loyalty/perks-refresh.logic";
import { markPprShellBuildDegraded } from "~/hec/ppr/ppr-context";
import { cacheBustToken } from "~/hec/utils/builder-cachebust";
import { stabilizeBuilderPixelIds } from "~/hec/utils/builder-pixel";
import {
  collectChromeSsrStyles,
  dedupeChromeSsrStyles,
  peekChromeSsrStyles,
} from "~/hec/utils/builder-ssr-styles";
import { slimBuilderEntry } from "~/hec/utils/builder-trim-references";
import { siteCodeToId } from "~/hec/utils/site-id";
import { retryOnceOnTransient } from "~/hec/utils/transient-error";
import type { Customer } from "~/lib/app.types";
import { ClientOnly } from "~/lib/ui";
import { invariant } from "~/lib/utils/utils";
import { isIseHost, isTpaSubdomainRequest } from "~/seo/utils/tpa-request";
import { upstreamSignal } from "~/utils/upstream-timeouts";

import {
  isAuthRevalidatingSubmit,
  isLeavingLoginPage,
} from "./auth-revalidate";
import { BasketProvider } from "./resources/basket";
import { AddedToBasketFlyout } from "./resources/components/added-to-basket-flyout";
import { FavoritesProvider } from "./resources/favorites";
import { SaveForLaterProvider } from "./resources/save-for-later-provider";
import { TpaBalanceProvider } from "./resources/tpa-balance-provider";

// COM-189 wave 4: the auth drawer (vaul + RVF/zod forms + Turnstile graph) and
// the TPA welcome modal render NOTHING during SSR — the drawer opens only via
// URL hash, the modal only from client-fetched balance data — so both load
// lazily, off the layout chunk the document modulepreloads. They mount on the
// overlays-ready signal; a `#login`-style hash landing mounts them immediately
// (see `openNow` below), and the drawer's own mount-time hash check then opens
// it, exactly as it did when statically imported.
const AccountDrawer = lazy(() =>
  import("~/components/account-drawer/account-drawer").then(m => ({
    default: m.AccountDrawer,
  })),
);
const TpaWelcomeModal = lazy(() =>
  import("~/hec/components/tpa-balance/welcome-modal").then(m => ({
    default: m.TpaWelcomeModal,
  })),
);

// `DrawerRendered` — the "remounted drawer rendered OPEN and survived" sentinel
// the drawer hosts via `renderedSentinel` — is imported from
// account-drawer-retry.tsx so the listener's unit tests exercise the shipped
// component (this module imports `cloudflare:workers` and cannot be).

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formMethod,
  formAction,
  currentUrl,
  nextUrl,
  actionStatus,
  actionResult,
  defaultShouldRevalidate,
}) => {
  // Honor an explicit `useRevalidator().revalidate()`: it carries no form
  // submission and doesn't change the URL. Callers include the logout flow, TPA
  // consent and the perks block, which need chrome to refresh in place without a
  // full reload. (Drawer sign-in no longer routes through here — it revalidates
  // via the auth-submit branch below.) Cross-page GET navigations (currentUrl !==
  // nextUrl) still fall through and skip this expensive loader (category tree +
  // Builder chrome fetches).
  if (
    !formMethod &&
    currentUrl.href === nextUrl.href &&
    defaultShouldRevalidate
  ) {
    return true;
  }
  // Successful /my-account POST (profile-name save → header greeting).
  // Substring, not prefix: matches the canonical /my-account mount (COM-21),
  // and a future re-mount of the layout under another prefix needs no edit
  // here. (The retired /cmp/my-account mount is a redirect-only splat with no
  // action, so a POST there is a 405 and never satisfies `actionStatus < 400`.)
  //
  // COM-830: basket POSTs deliberately do NOT revalidate this loader. Nothing
  // it returns reads the basket (the minicart, flyout and cart-spend readers
  // all hang off BasketProvider's own `/resources/basket` fetcher), so the old
  // boilerplate `/basket` rule only re-paid getCustomer + chrome cache reads on
  // every registered add-to-cart.
  if (
    formMethod === "POST" &&
    !!formAction?.includes("/my-account") &&
    !!actionStatus &&
    actionStatus < 400
  ) {
    return true;
  }
  // SFCC-11869 — re-run the loaders that drive signed-in chrome after a drawer
  // sign-in/registration, and after the sign-out POST. D4 made /logout a POST
  // action; a form POST never puts /logout into currentUrl/nextUrl — it only
  // appears as formAction — so the pathname check below cannot see it (shared
  // with root.tsx so the two cannot drift).
  if (isAuthRevalidatingSubmit({ formMethod, formAction, actionStatus }))
    return true;
  // COM-798: a deadline-tracker save or Learning Center award that granted
  // points moved the balance and tier the header dropdown and cart banner read
  // (shared with root.tsx so the two cannot drift).
  if (
    isPerksMutationSubmit({
      formMethod,
      formAction,
      actionStatus,
      actionResult,
    })
  )
    return true;
  // Leaving the `/account` login page (COM-7, .16 flip): this loader owns
  // `isLoggedIn` for the header, and the post-login revalidation round is
  // short-circuited by the login page's own loader redirect — so the hop to
  // `/my-account` must re-run it (predicate docblock has the measurement).
  if (isLeavingLoginPage({ currentUrl, nextUrl })) return true;
  // Legacy GET /logout bookmark redirect (the loader redirects home without
  // mutating); the sign-out POST is matched by the formAction branch above.
  if (currentUrl.pathname.includes("/logout")) {
    return true;
  }

  return false;
};

export const loader = async ({ request, context }: LoaderArgs) => {
  let isLoggedIn = false;
  let user = null;

  try {
    // `isAuthenticatedUser()` reads identity from Auth and awaits `ready()` only
    // when this request adopts a divergent inbound SLAS cookie (SFCC-11092), so
    // a cookie-adopted registered header resolves correctly without forcing a
    // SLAS round-trip on anonymous loads.
    isLoggedIn = await isAuthenticatedUser();
    user = await optionalAuthenticatedUser();
  } catch (e) {
    // Auth failure (stale token, SLAS unreachable) — continue as guest
    console.warn(
      "[Layout] Auth check failed, continuing as guest:",
      e instanceof Error ? e.message : e,
    );
  }

  // Meganav tree is site-wide and changes rarely. Cache it on the edge
  // (24h SWR) and do NOT await it on TTFB — the
  // header / breadcrumbs / PLP pills stream it via <Await>. A cold miss
  // is shop.getCategory("root") + SLAS ready(); a hit skips SCAPI.
  const categoryNav = cachedCategoryNav("[Layout]");
  const categoryTree = categoryNav.then(nav => nav.categoryTree);
  const categorySlugMap = categoryNav.then(nav => nav.categorySlugMap);
  const categoryAltUrlMap = categoryNav.then(nav => nav.categoryAltUrlMap);

  // Fetch Builder content in parallel
  const apiKey = getBuilderConfig(context.preset).api_key;
  // Without `userAttributes` Builder skips start/end-date scheduling (COM-460).
  const scheduling = encodeURIComponent(
    JSON.stringify({ useScheduling: true }),
  );
  const fetchBuilderModel = async (model: string) => {
    try {
      // `includeRefs=true` inlines referenced entries' data as
      // `reference.value.data` (e.g. each nav `Link`'s `external-links` /
      // `page` target). Without it, header CTA links can't resolve their
      // href — `useReference()` only reads the per-route `references` map,
      // which the layout loader never populates — so every link falls back
      // to "/" (SFCC-11308). `fetchBuilderModelByName` already passes this.
      // `cachebust`: skip Builder's edge cache (`stale-while-revalidate=86400`)
      // — an unbusted fetch can return day-old chrome after a publish
      // (SFCC-10767). Time-bucketed so a publish lands within one bucket while
      // preserving Builder's CDN as a fallback during origin brown-outs (an
      // always-unique token removed that fallback and dropped sitewide chrome).
      const res = await fetch(
        `https://cdn.builder.io/api/v3/content/${model}?apiKey=${apiKey}&limit=1&includeRefs=true&userAttributes=${scheduling}&cachebust=${cacheBustToken()}`,
        { signal: upstreamSignal("builder") },
      );
      if (res.ok) {
        const json = (await res.json()) as { results?: unknown[] };
        if (Array.isArray(json?.results)) {
          // Slim page-ref data + editor metadata before the chrome tree is
          // serialized into every page. See ~/hec/utils/builder-trim-references.
          return slimBuilderEntry(json.results[0] ?? null);
        }
        console.warn(
          `[Layout] Builder ${model} returned 200 with no results array`,
        );
      } else {
        console.warn(
          `[Layout] Builder ${model} fetch failed: HTTP ${res.status}`,
        );
      }
    } catch (e) {
      console.warn(
        `[Layout] Builder ${model} fetch failed:`,
        e instanceof Error ? e.message : e,
      );
    }
    // COM-460: a failure is not "no entry"; a cached null blanked the affiliate bar.
    return BUILDER_FETCH_FAILED;
  };

  // Fetch + transform the TPA welcome banner when an AFID cookie is present
  // (SFCC-11087). Cached fetchOneEntry wrapper with the AFID embedded in the SWR
  // key. NOTE: the dotted `query.data.tpaId=` raw-API param does NOT match
  // (verified against the live model) — the SDK `query` serializes to the
  // working JSON form. Relative image srcs are remapped to the SFCC static CDN;
  // the authored `<style>` block is preserved downstream via
  // `sanitizeHtml(..., { allowStyle: true })`. Optional marketing content, so
  // degrade to null on any Builder error.
  const fetchTpaBanner = async (
    afidValue: string | null,
    baseImageUrl: string,
  ): Promise<string | null> => {
    if (!afidValue) return null;
    const entry = await swrFetchOneEntry(
      {
        model: ModelMap.tpaWelcomeBanner,
        apiKey,
        query: { "data.tpaId": afidValue },
      },
      {
        name: "tpa-welcome-banner",
        deps: [afidValue],
        tags: e =>
          layoutCacheTags(CHROME_GROUP_TAG.tpaBanner, [
            { model: ModelMap.tpaWelcomeBanner, entry: e },
          ]),
      },
    ).catch(e => {
      // Optional marketing content — degrade to the standard bar, but keep the
      // brown-out visible (parity with the sibling Builder fetches' warn).
      console.warn(
        "[Layout] TPA banner fetch failed:",
        e instanceof Error ? e.message : e,
      );
      return null;
    });
    const bannerContent = (
      entry?.data as { bannerContent?: string } | undefined
    )?.bannerContent;
    return bannerContent
      ? remapTpaBannerHtml(bannerContent, baseImageUrl)
      : null;
  };

  const hostname = new URL(request.url).hostname;

  // Surface A: tpa.* subdomain loaded as iframe inside a TPA partner portal.
  // Suppresses HEC chrome (GLOBAL-50); root.tsx mounts the link-escape runtime
  // that opens every framed click in a new tab (COM-107).
  const isTpaSubdomain = isTpaSubdomainRequest(
    request,
    context.preset.instance,
  );

  // ISE soft-gate (CART-31): single source of the cart/mini-cart guard. On an
  // `ise.*` host a shopper without an active Alegeus TPA session is a guest
  // (the SSO logs TPA users in as registered customers, so `!isLoggedIn` is
  // the faithful proxy). Computed here because layout is the one place that
  // resolves both auth state and hostname at SSR — consumed by cart.tsx and
  // the mini-cart via `useLayoutData()`/`useRouteLoaderData`.
  const isISEAndGuest = isIseHost(hostname) && !isLoggedIn;

  // TPA/LMN suppression — must be before Promise.all since fetchTpaBanner uses afid.
  // Resolve from the URL param / tpaBranding cookie first; fall back to the AFID
  // persisted in the KV session (TPA-09) so a returning TPA user keeps their
  // identity even after the tpaBranding cookie is cleared.
  const afid = getAfidFromRequest(request) ?? getSession().get("afid") ?? null;

  // SFCC-11087: base for remapping the TPA banner's relative image srcs to the
  // SFCC static CDN. On the local dev server (localhost) point at the
  // production-mirroring devhec host so images resolve; any deployed host
  // (devhec/staging/prod) proxies `/on/demandware.static` on its own origin.
  const tpaBannerImageBase = `${
    hostname === "localhost" || hostname === "127.0.0.1"
      ? `https://${context.siteCode}.devhec.com`
      : new URL(request.url).origin
  }/on/demandware.static/-/Sites/default/`;

  // SFCC-12061: like `fetchBuilderModel` above, this fetcher separates
  // "Builder answered and no entry matches" (`null` — a real answer, safe to
  // cache) from "the fetch failed" (`BUILDER_FETCH_FAILED` — network error /
  // timeout / non-200, must never be cached). `classifyChromeFanout` uses the
  // sentinel to decide whether the shared-chrome cache write is skipped.
  const fetchBuilderModelByName = async (model: string, name: string) => {
    try {
      // `cachebust`: same time-bucketed rationale as `fetchBuilderModel` above
      // (SFCC-10767). Retried once on a dropped connection, other transient,
      // AND a timeout (unlike `swrFetchOneEntry`, COM-837): the first fetch in
      // a new cache-bust bucket reaches Builder's origin (measured up to 3.1 s
      // for the account dropdown, COM-482), and without the retry that cold
      // MISS skips the 24 h chrome cache write and degrades the PPR shell after
      // every deploy. The retry is cheap here: this runs on the once-per-colo
      // MISS, and SWR single-flight already holds the fan-out to one per
      // isolate, so it is not the N-way 'Main Footer' stampede COM-837 removed.
      // The body read is inside the retry because the timeout also aborts a
      // slow body — where a large entry spends its time.
      const { res, json } = await retryOnceOnTransient(async () => {
        const response = await fetch(
          `https://cdn.builder.io/api/v3/content/${model}?apiKey=${apiKey}&limit=1&includeRefs=true&query.name=${encodeURIComponent(name)}&userAttributes=${scheduling}&cachebust=${cacheBustToken()}`,
          { signal: upstreamSignal("builder") },
        );
        const body = response.ok
          ? ((await response.json()) as { results?: unknown[] })
          : null;
        return { res: response, json: body };
      }, `layout:${model}:${name}`);
      if (res.ok) {
        // A 200 without a `results` array is NOT "no entry exists" — it's a
        // malformed answer (error envelope, proxy rewrite, schema change) and
        // must fall through to the sentinel so it is never cached.
        if (Array.isArray(json?.results)) {
          // Slim page-ref data + editor metadata before the chrome tree is
          // serialized into every page. See ~/hec/utils/builder-trim-references.
          return slimBuilderEntry(json.results[0] ?? null);
        }
        console.warn(
          `[Layout] Builder ${model} (name=${name}) returned 200 with no results array`,
        );
      } else {
        console.warn(
          `[Layout] Builder ${model} (name=${name}) fetch failed: HTTP ${res.status}`,
        );
      }
    } catch (e) {
      console.warn(
        `[Layout] Builder ${model} (name=${name}) fetch failed:`,
        e instanceof Error ? e.message : e,
      );
    }
    return BUILDER_FETCH_FAILED;
  };
  const [sharedContent] = SWR.stable(
    // SFCC-12061: `-v2` orphans the pre-fix entries that cached a null footer.
    // The key's version discriminator is the CLIENT manifest hash, and this fix
    // is server-only — without the bump a deploy might not rotate the key and
    // poisoned entries would keep serving a blank footer for up to 24h.
    "layout-shared-content-v2",
    ["layout", "nav", "footer", "tpa-banner"],
    // COM-460: 15-min fresh so a Builder schedule boundary lands within one
    // window; the refresh runs in the background (stale-while-revalidate).
    BUILDER_PAGE_TREE_CACHE_CONTROL,
    true,
    // COM-837: a degraded chrome (a critical leg failed) is returned as a real
    // value so single-flight can share it, and is vetoed from the 24h write.
    { shouldCache: data => !isDegradedChrome(data) },
  );

  // Timed so the shared-chrome fan-out (nav/footer/TPA banner/account dropdown +
  // LMN gate) shows up as `layout-shared-content` in the Server-Timing header.
  // On a warm SWR cache this resolves near-instant; cold, it's the parallel
  // Builder + LMN-pref latency this loader contributes to TTFB.
  const { time } = getTimingContext();

  // The shared-chrome fan-out, cached as ONE array under the SWR entry
  // above (15 min fresh, 24h stale). Member order is load-bearing (the destructure after `time(...)`,
  // the tag builder, and FOOTER_FANOUT_INDEX all index into it) and is
  // declared by CHROME_FANOUT_ORDER in layout-chrome.server.ts — reorder
  // THERE first, then mirror it here; the labels below name each slot.
  const fetchChromeFanout = () =>
    Promise.all([
      /* navDrawer */ fetchBuilderModel(ModelMap.navDrawerContent),
      /* affiliateNav */ fetchBuilderModel(ModelMap.affiliateNavBar),
      /* tpaBanner */ fetchTpaBanner(afid, tpaBannerImageBase),
      /* accountDropdown */ fetchBuilderModelByName(
        ModelMap.popoverContent,
        "Account Dropdown",
      ),
      // Shared Builder-driven footer. Null only when Builder answers and no
      // "Main Footer" entry exists — no hardcoded fallback by design (prefer
      // empty over stale legal links). A FAILED fetch returns the
      // BUILDER_FETCH_FAILED sentinel instead, which skips the cache write
      // below so one Builder brown-out can't pin a missing footer for the
      // full 24h TTL (SFCC-12061).
      /* footer */ fetchBuilderModelByName(ModelMap.footer, "Main Footer"),
      // LMN-visibility gate (SFCC-11059 TPA-03 master switch + SFCC-11060 TPA-04
      // allow-list). Both prefs are cache-backed (same site-prefs set already
      // fetched for PowerReviews/GTM/etc.) and read in parallel inside the helper;
      // falls back to the LMN_ALLOWED_TPAS env var where the pref isn't set.
      /* lmnAllowed */ resolveLmnAllowed({ afid }),
      // TPA-12: `lmnEnable` master switch for LMN *nav links* (cache-backed pref).
      /* lmnEnable */ isLmnNavEnabled(),
      // SFCC-11677: LMN tile/modal content from the Builder `site-preferences`
      // entry. Fetched once here (edge-cached SWR), exposed via useLayoutData()
      // so the LMN tile + "What's an LMN?" modal render from loaded data with
      // no per-open Builder call.
      /* lmnContent */ getLmnTileContent(apiKey),
    ]);

  // SFCC-12061: when a critical fetch fails inside the SWR callback, the
  // callback returns the DEGRADED chrome (failed members coerced to null, plus
  // a trailing DEGRADED_CHROME_MARK) and `shouldCache` vetoes the 24h write (a
  // cached null footer used to blank the footer sitewide for up to a day).
  // COM-837: because the degraded value is a plain returned value (no
  // request-scoped stash), single-flight shares it with concurrent callers —
  // one 8-leg fan-out per isolate during a Builder brown-out, not one per
  // request. The next flight retries.

  // COM-175: the magic-link prefs read is joined into ONE await with the
  // chrome fan-out — the legs are mutually independent (chrome/alert bar hit
  // Builder + the shopper-agnostic site-pref cache; magic-link prefs ride the
  // same AM-token-backed pref cache, no shopper SLAS state touched), so
  // serializing them just added a cold-prefs round-trip to TTFB. Error
  // semantics are unchanged: `getMagicLinkConfig` never rejects (it reads via
  // `getSitePreferenceSafe`, which swallows + defaults per-pref), so this
  // Promise.all can only reject where the old sequential chrome await already
  // did. It sits OUTSIDE the `time(...)` wrap so the `layout-shared-content`
  // Server-Timing metric keeps measuring exactly the chrome fan-out + alert
  // bar (COM-175 item 2 relies on that signal staying pure).
  // ET-225..232: Builder-authored telehealth tile pricing. Started HERE, before
  // the chrome await, so it runs in parallel without joining the fan-out below
  // — that fan-out is an edge-cached POSITIONAL TUPLE, so appending a leg would
  // leave every already-cached array one element short (an `undefined` price on
  // every telehealth tile) until its 24h TTL rolled. Its own `SWR.stable` cache
  // also gives a price change a 15-min ceiling rather than a day — and holds to
  // it, because that fetch busts Builder's own CDN (whose
  // stale-while-revalidate would otherwise stretch the effective ceiling well
  // past a day; see `telehealth-pricing.server.ts`).
  //
  // Safe to leave floating until awaited below: the helper never rejects (its
  // only upstream call carries `.catch`, and normalization is pure), so this
  // cannot become an unhandled rejection.
  const telehealthPricingPromise = getTelehealthTilePricing(apiKey);

  const [[sharedChrome, alertBarContent], magicLink] = await Promise.all([
    time(
      () =>
        Promise.all([
          sharedContent(
            async () => {
              const { chrome, criticalFailed } = classifyChromeFanout(
                await fetchChromeFanout(),
                CRITICAL_CHROME_FANOUT_INDICES,
              );
              if (criticalFailed) {
                // Distinct from the per-fetch warns: a run of these means a
                // sustained Builder outage is bypassing the chrome cache on
                // every request (the accepted SFCC-12061 trade) — alert on it.
                console.warn(
                  "[Layout] shared-chrome cache write SKIPPED — a critical Builder fetch (footer / affiliate nav / account dropdown) failed",
                );
                // A background REVALIDATING refresh's return is not cached
                // either (stale chrome keeps serving). Every foreground caller —
                // leader or single-flight joiner — marks its own shell build
                // degraded below, off the trailing mark.
                return [
                  ...chrome,
                  DEGRADED_CHROME_MARK,
                ] as unknown as typeof chrome;
              }
              return chrome;
            },
            [afid, tpaBannerImageBase],
            // Group tag `builder-layout-shared` (admin purge preset) + umbrella +
            // an entry tag `builder-{model}-{id}` per bundled entry (webhook
            // auto-purge on publish). tpaBanner (index 2, remapped HTML) + the LMN
            // gates (5–7) carry no id, so contribute no entry tag.
            data => {
              // Null data = a critical chrome fetch failed (never cached —
              // footer or affiliate nav; see the skip warn above). Guard is
              // required: `runAndCache` calls setTags BEFORE its null check, so
              // destructuring null here would throw — a 500 on the MISS path,
              // and on the REVALIDATING path SWRValue's catch would DELETE the
              // key, evicting the good stale chrome this fix exists to keep.
              if (!data) {
                // Nothing is being cached — no tags.
                return [];
              }
              const [nav, affiliate, , accountDropdown, footer] = data;
              return layoutCacheTags(CHROME_GROUP_TAG.layoutShared, [
                { model: ModelMap.navDrawerContent, entry: nav },
                { model: ModelMap.affiliateNavBar, entry: affiliate },
                { model: ModelMap.popoverContent, entry: accountDropdown },
                { model: ModelMap.footer, entry: footer },
              ]);
            },
          ).then(
            // Pixel ids on READ too: chrome cached under the 24 h entry before
            // the fetch-time rewrite keeps Builder's random ids (COM-482).
            chrome => (chrome ? stabilizeBuilderPixelIds(chrome) : chrome),
          ),
          // SFCC-11742: the alert bar is fetched STANDALONE — outside the
          // `layout-shared-content` SWR wrap — with its own 15-min `swrFetchOneEntry`
          // TTL (the shared wrap was 24h then; 15 min since COM-460), matching the
          // PDP/PLP section fetches. Routing through `fetchAlertBarContent`
          // (→ `swrFetchOneEntry`) is also what restores the `userAttributes` Builder
          // needs to evaluate start/end-date scheduling at all. Runs in parallel with
          // the shared-chrome fan-out.
          fetchAlertBarContent(apiKey),
        ]),
      {
        type: "layout-shared-content",
        desc: "Layout shared chrome + LMN gate + alert bar",
      },
    ),
    // Magic-link config for the forgot-password form (SFCC-11521). Cached
    // prefs; drives whether the "Email Sign In Link" button shows + the
    // rate-limit copy. Infallible by contract — see the COM-175 note above.
    getMagicLinkConfig(),
  ]);

  // Started before the chrome await, so this is already resolved in practice.
  // Degrades to empty pricing internally — telehealth tiles then render no price.
  const telehealthPricing = await telehealthPricingPromise;

  // A cache hit, the leader's callback and a single-flight joiner all hand back
  // the array (degraded or not) — so this only throws on an SWR contract
  // change, never in normal operation.
  const chromeData = sharedChrome;
  invariant(chromeData, "layout shared-chrome fan-out resolved empty");
  if (isDegradedChrome(chromeData)) {
    // COM-482: a shell built from this render would bake the failed member's
    // fallback into every guest's document. The build refuses to cache it; the
    // next build retries. Runs for joiners too (they never ran the callback).
    markPprShellBuildDegraded(context.pprShellBuild, "layout-chrome");
  }
  const [
    navDrawerContent,
    affiliateNavBar,
    tpaBannerContent,
    accountDropdownContent,
    footerContent,
    lmnAllowed,
    lmnEnable,
    lmnContent,
  ] = chromeData;

  const siteCode = context.siteCode;

  // Wave 3 flight diet: generate the chrome trees' per-block CSS server-side
  // and STRIP `responsiveStyles` from the trees before they serialize into
  // the flight payload (67.5KB across the six trees, on every route). The
  // stripped trees render identical block markup on both sides; AppLayout
  // re-emits the extracted CSS as the same hoisted `<style href precedence>`
  // resources the SDK would have produced. Memoized by entry identity, so an
  // in-memory-cached tree is only walked once. tpaBannerContent is remapped
  // HTML (no blocks) and is deliberately not included.
  // See ~/hec/utils/builder-ssr-styles.ts for the full contract + trade-offs.
  const chromeStyles = collectChromeSsrStyles([
    navDrawerContent,
    affiliateNavBar,
    accountDropdownContent,
    footerContent,
    alertBarContent,
    lmnContent,
  ]);

  // PR #1042 blocker 5: on a DATA request (client revalidation after a
  // account/auth POST, or `useRevalidator()`), the fresh chrome trees can
  // differ from what this document's head carries (scheduled alert-bar swap,
  // chrome publish) — and they arrive stripped, so without their CSS the new
  // blocks render unstyled for the rest of the session. Ship the extracted
  // styles in loader data on that path only; AppLayout re-emits them as
  // href-deduped precedence styles, so React inserts exactly the new ones.
  // Document requests keep `null` — their styles ride the SSR head, and the
  // flight payload keeps the full Wave-3 diet. Discriminator: browser
  // document navigations Accept text/html; RR7 single-fetch data requests
  // Accept */* — the same rule the guest HTML cache keys eligibility on
  // (cloudflare/guest-html-cache.ts). With WORKERS_PPR_MODE on, Fetch
  // Metadata navigation counts too, matching the PPR seam (COM-901).
  const wantsDocument = isLayoutDocumentRequest(
    request,
    (env as { WORKERS_PPR_MODE?: string }).WORKERS_PPR_MODE === "on",
  );
  const chromeSsrStyles = wantsDocument ? null : chromeStyles;

  // Public keys safe to expose to client
  const turnstileSiteKey = env.TURNSTILE_SITE_KEY ?? "";
  // DeferredHydration gate (SFCC-11887 re-enable, fail-closed). Resolved
  // server-side and shipped via loader data ON PURPOSE: the gate changes SSR
  // HTML structure, so client hydration must see exactly the value this
  // render used — see app/components/deferred-hydration.tsx.
  const deferHydration = isEnabled(resolveDeferHydrationMode(env).mode);

  return {
    isLoggedIn,
    isISEAndGuest,
    user,
    categoryTree,
    categorySlugMap,
    categoryAltUrlMap,
    hostname,
    isTpaSubdomain,
    navDrawerContent,
    affiliateNavBar,
    lmnAllowed,
    lmnEnable,
    lmnContent,
    telehealthPricing,
    alertBarContent,
    tpaBannerContent,
    siteCode,
    accountDropdownContent,
    turnstileSiteKey,
    enableMagicLink: magicLink.enabled,
    magicLinkTimeout: magicLink.timeoutMinutes,
    footerContent,
    deferHydration,
    chromeSsrStyles,
  };
};

/**
 * Hook to read this layout route's loader data from any component nested
 * under it. Mirrors `useRootLayoutData()` at `root.tsx:163`. Use for
 * `categorySlugMap` (URL slug rewrites) and other layout-scoped state that
 * isn't part of the root loader.
 *
 * Route id `"routes/layout"` matches the file-path-derived id React Router
 * assigns to `layout("./routes/layout.tsx", ...)` in `app/routes.ts`.
 */
export const useLayoutData = () => {
  const data = useRouteLoaderData<typeof loader>("routes/layout");
  invariant(data, "useLayoutData must be used within app layout");
  return data;
};

export default function AppLayout() {
  const ContentHeroSlot = useSlot("contentRootHeroSlot");
  const ContentBotomSlot = useSlot("contentRootBottomSlot");
  const {
    isLoggedIn,
    user,
    categoryTree,
    hostname,
    isTpaSubdomain,
    navDrawerContent,
    affiliateNavBar,
    alertBarContent,
    tpaBannerContent,
    siteCode,
    accountDropdownContent,
    turnstileSiteKey,
    enableMagicLink,
    magicLinkTimeout,
    footerContent,
    lmnContent,
    deferHydration,
    chromeSsrStyles: loaderChromeStyles,
  } = useLoaderData<typeof loader>();

  // Wave 3 flight diet: re-emission of the chrome trees' block CSS (extracted
  // + stripped by the loader). Server render: the loader-filled memo (`peek`)
  // returns the styles and React hoists/coalesces them into the same single
  // `<style data-precedence>` head tag the SDK produced before. Client
  // hydration render: both sources are empty (memo is server-side; document
  // loader data ships `chromeSsrStyles: null`) and nothing renders — React 19
  // hydrates hoistable styles by href adoption and never removes
  // server-rendered ones a client render doesn't claim. Client REVALIDATION
  // (PR #1042 blocker 5): the data response carries the styles in loader
  // data, and rendering them here inserts exactly the new hrefs into the
  // head — a swapped alert bar / published footer stays styled mid-session.
  const chromeSsrStyles = dedupeChromeSsrStyles(
    peekChromeSsrStyles([
      navDrawerContent,
      affiliateNavBar,
      accountDropdownContent,
      footerContent,
      alertBarContent,
      lmnContent,
    ]),
    loaderChromeStyles,
  );

  // Child routes can opt out of the shared layout chrome by returning
  // `hideLayoutHeader` / `hideLayoutFooter` from their loader. The article
  // and builder catch-all routes use `hideLayoutFooter` for pages whose
  // Builder content embeds its own footer (via `useDefaultFooter: false`).
  //
  // `hideLayoutHeader` has two consumers:
  //  - The telehealth intake routes (ET-245, `app/hec/telehealth/intake-chrome.tsx`),
  //    which mount their own minimal "logo + lock icon" header via the
  //    `contentRootHeroSlot` handle slot below instead of the native `HecHeader`.
  //  - Builder pages (SFCC-11840), set ONLY when a page sets
  //    `useDefaultHeader: false` AND embeds no header Symbol of its own — i.e.
  //    it genuinely wants no header. A page that opts out but DOES embed a
  //    header still gets the native HecHeader, because the embedded header's
  //    components aren't ported yet (Task B) and rendering nothing at all would
  //    be worse than rendering the native chrome.
  const { hideLayoutHeader, hideLayoutFooter } = useLayoutChromeFlags();
  const location = useLocation();
  // Back-to-top is suppressed on the homepage (SFCC-11673).
  const isHomePage = location.pathname === "/";

  // Lazy-overlay gate (COM-189 wave 4, see the lazy() consts above). A landing
  // that carries an auth-drawer hash (magic-link emails, `requireAccountUser`
  // redirects) skips the idle wait so the drawer opens without an interaction.
  const overlaysReady = useOverlaysReady({
    openNow: () => parseDrawerHash(window.location.hash) !== null,
  });

  // PR #1141 (COM-189 wave 4 follow-up): when the drawer chunk fails to
  // import, the boundary reports it here and `AccountDrawerRetryOnHash` takes
  // over the hash listening the (never-mounted) drawer would have done, so the
  // shopper's next "Sign in" tap retries instead of silently doing nothing. A
  // remount (non-module crash) bumps the token, which clears the boundary
  // latch. ONE remount per FAILURE: `drawerRemount` is set when the remount
  // happens, and while the drawer keeps failing the retry escalates to the
  // `/account` page instead of remounting again — a deterministic render
  // crash with `#login` in the URL must not cycle failure → remount → failure.
  // It re-arms once the remounted drawer has rendered OPEN and survived that
  // commit (`DrawerRendered`, hosted inside the drawer's open content
  // via `renderedSentinel` and microtask-deferred — NOT on the drawer chunk
  // resolving, NOT on the closed shell committing, NOT on the sentinel's own
  // mount), the layout's equivalent of the header twins re-arming on a click
  // that reaches the working overlay: a transient crash, a tap, a working
  // drawer, and a LATER unrelated crash on the same page must get its own
  // in-place remount, not a hard `/account` navigation the shopper never
  // asked for. A remount is only ever planned off an auth hash, and the
  // remounted drawer opens on that same hash, so a working remount always
  // reaches the sentinel (the `#forgot-password` hash included — the drawer
  // hosts it in the forgot-password modal as well, so a remount that opens
  // the modal re-arms too). A soft nav to a NEW pathname drops it too — by
  // DERIVATION, not by an effect (round 7): the bound is the `{ pathname,
  // hash }` captured at remount time and counts only while
  // `location.pathname` still matches (`activeDrawerRemount`). A
  // `useEffect(() => reset, [location.pathname])` ran in the SAME
  // passive-effects flush as the listener's `onRemount` when the remount was
  // triggered by a cross-pathname soft nav to an auth hash (a guest's
  // `<Link to="/account">` whose loader redirects to `/#login?redirectTo=…`,
  // arriving from `/cart` with the boundary already latched): React runs
  // child effects first, so the listener set the bound and this parent's
  // effect then queued null over it in the same batch — the remount was
  // recorded as never having happened, the re-failure parked silently (hash
  // stripped, a dead landing) and `/account` came one tap late, after a
  // SECOND in-place remount. A stale entry (another pathname) is inert; it
  // can only count again if the shopper soft-navs back to that pathname with
  // the SAME failure still latched, and the one remount for that failure has
  // then already been spent, so escalating on that tap is the contract.
  // NOT keyed on `location.key`: a plain `<a href="#login">` tap pushes a
  // history entry with null state, which RR7 keys as "default" — the same
  // key as the landing — so a key-equality bound never saw the first anchor
  // tap's remount.
  //
  // The bound carries the HASH the remount was planned off, not just a flag
  // (round 6): the remounted drawer's hash effect clears the hash
  // (`replaceState`) in the same effect that schedules `setOpen(true)`, BEFORE
  // the open body renders and throws, so the fresh listener that mounts for
  // the re-failure finds `window.location.hash` empty. Handed the captured
  // hash it escalates to `/account` on the SAME tap; off the live hash alone
  // the tap was a silent remount → crash and `/account` only came on the next
  // one.
  const [drawerFailure, setDrawerFailure] = useState<OverlayFailure | null>(
    null,
  );
  const [drawerRetryToken, setDrawerRetryToken] = useState(0);
  const [drawerRemount, setDrawerRemount] = useState<{
    pathname: string;
    hash: string;
  } | null>(null);
  // The bound that counts HERE: the one remount, if it was planned at this
  // pathname. Derived per render (round 7, see above) — a remount planned at
  // another pathname is inert.
  const activeDrawerRemount =
    drawerRemount !== null && drawerRemount.pathname === location.pathname
      ? drawerRemount
      : null;
  const remountDrawer = useCallback(() => {
    setDrawerRemount({
      pathname: location.pathname,
      hash: window.location.hash,
    });
    setDrawerFailure(null);
    setDrawerRetryToken(token => token + 1);
  }, [location.pathname]);
  const rearmDrawer = useCallback(() => setDrawerRemount(null), []);

  const layoutTree = (
    <>
      {/* Hoisted chrome CSS (SSR only — see peekChromeSsrStyles above).
          `href` + `precedence` make each one a React 19 hoistable style
          resource: coalesced into the head precedence tag and deduped
          document-wide by href, exactly like the SDK-emitted originals. */}
      {chromeSsrStyles.map(s => (
        <style
          key={s.href}
          href={s.href}
          precedence="default"
          data-id="builderio-block"
          dangerouslySetInnerHTML={{ __html: s.css }}
        />
      ))}
      <AuthProvider
        isLoggedIn={isLoggedIn}
        user={(user as Customer | undefined) ?? null}
      >
        <MediaScreenProvider>
          <SaveForLaterProvider>
            <BasketProvider>
              <FavoritesProvider>
                <TpaBalanceProvider>
                  {/*
              vaul-drawer-wrapper marks the element Vaul scales back when a
              Drawer opens with shouldScaleBackground={true} (mobile nav,
              PLP filters drawer). Without this attribute the prop is a no-op.
              Drawers render in a Portal under <body>, so they sit outside
              this wrapper — exactly what we want. The literal attribute name
              is what Vaul's `[vaul-drawer-wrapper]` selector targets, so it
              can't be renamed (e.g. to a `data-*` form).
            */}
                  <div
                    // eslint-disable-next-line react/no-unknown-property
                    vaul-drawer-wrapper=""
                    className="flex min-h-dvh flex-col"
                  >
                    <a
                      href="#maincontent"
                      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:p-2 focus:text-black"
                    >
                      Skip to main content
                    </a>
                    {!hideLayoutFooter && !isTpaSubdomain && (
                      <a
                        href="#footercontent"
                        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:p-2 focus:text-black"
                      >
                        Skip to footer
                      </a>
                    )}
                    {!hideLayoutHeader && !isTpaSubdomain && (
                      <HecHeader
                        isLoggedIn={isLoggedIn}
                        user={user as Customer | undefined}
                        categories={categoryTree}
                        hostname={hostname}
                        navDrawerContent={navDrawerContent}
                        affiliateNavBar={affiliateNavBar}
                        alertBarContent={alertBarContent}
                        tpaBannerContent={tpaBannerContent}
                        siteCode={siteCode}
                        accountDropdownContent={accountDropdownContent}
                      />
                    )}
                    <div
                      id="blurrable-content"
                      className="flex min-h-0 flex-1 flex-col"
                    >
                      {/* HERO Banner */}
                      <ContentHeroSlot />
                      {/* -- / -- HERO Banner end */}
                      {/* flex-1 keeps the footer below the fold when the outlet
                      is empty (client nav to a slim Builder page before the
                      tree fetch lands). */}
                      <main id="maincontent" className="flex-1">
                        <Outlet />
                      </main>
                      {/* Footer --- */}
                      <ContentBotomSlot />
                      {!hideLayoutFooter && !isTpaSubdomain && (
                        <footer id="footercontent">
                          {/* Put NO boundary of any kind around this subtree.
                          Both incidents here were boundary-shaped. SFCC-12061:
                          an enclosing `fallback={null}` Suspense/Await turned a
                          hydration abandonment (a sync provider state update
                          reaching the not-yet-hydrated boundary) into a visible
                          footer blank — and `footerContent` is a plain awaited
                          value, so it needs no Await anyway. COM-186: the
                          `DeferredHydration` gate did the same thing at full
                          subtree scale (~550 ms blank, CLS 0.303). See
                          components/deferred-hydration.tsx and
                          docs/working-conventions.md. */}
                          <BuilderFooter entry={footerContent} />
                        </footer>
                      )}
                    </div>
                  </div>
                  {/* SFCC-11673: global back-to-top (SFRA footer parity). Mounted
                  outside #blurrable-content so it floats above page content
                  (incl. the footer) but below modals/drawers. Suppressed on
                  checkout/TPA (footer's own gate) and on the homepage. */}
                  {!hideLayoutFooter && !isTpaSubdomain && !isHomePage && (
                    <BackToTop />
                  )}
                  <AddedToBasketFlyout />
                  {/* Both SSR to nothing (see the lazy() consts above), so the
                    ready gate changes no server-rendered byte — it only keeps
                    their chunks out of the initial request wave. */}
                  {overlaysReady && (
                    <>
                      {/* Each lazy overlay rides its own OverlayBoundary (PR
                        #1042 blocker 3): a rejected chunk import must degrade
                        THAT overlay to nothing, not unmount the page into
                        RootErrorBoundary. */}
                      <OverlayBoundary
                        overlay="account-drawer"
                        fallback={null}
                        onError={setDrawerFailure}
                        retryToken={drawerRetryToken}
                      >
                        <Suspense fallback={null}>
                          <AccountDrawer
                            siteId={siteCodeToId[siteCode]}
                            turnstileSiteKey={turnstileSiteKey}
                            enableMagicLink={enableMagicLink}
                            magicLinkTimeout={magicLinkTimeout}
                            renderedSentinel={
                              <DrawerRendered onRendered={rearmDrawer} />
                            }
                          />
                        </Suspense>
                      </OverlayBoundary>
                      {drawerFailure && (
                        <AccountDrawerRetryOnHash
                          failure={drawerFailure}
                          onRemount={remountDrawer}
                          remountAttempted={activeDrawerRemount !== null}
                          remountHash={activeDrawerRemount?.hash}
                        />
                      )}
                      {/* TPA first-visit welcome modal (SFCC-11427) — global, all
                        viewports; consumes balanceDisplayData.popup via the shared
                        tpa-balance fetcher, gated client-side per session. */}
                      <OverlayBoundary
                        overlay="tpa-welcome-modal"
                        fallback={null}
                      >
                        <Suspense fallback={null}>
                          <TpaWelcomeModal />
                        </Suspense>
                      </OverlayBoundary>
                    </>
                  )}
                  {/* TPA Learning Center consent modal (SFCC-11508). Client-only:
                  the render guard reads localStorage + the current route, so it
                  must not render during SSR (would flash the modal pre-hydration
                  / mismatch). Self-gated to LC routes whose URL carries both
                  employer_id + employee_id, for shoppers who haven't yet
                  answered (COM-504). */}
                  <ClientOnly>
                    {() => <TpaLearningCenterConsentGate />}
                  </ClientOnly>
                </TpaBalanceProvider>
              </FavoritesProvider>
            </BasketProvider>
          </SaveForLaterProvider>
        </MediaScreenProvider>
      </AuthProvider>
    </>
  );

  return (
    // Seeds every <DeferredHydration> in this layout's tree (the footer above,
    // the Builder body in the catch-all + learning-center child routes) with
    // the loader-resolved WORKERS_DEFER_HYDRATION_MODE value. Loader data is
    // serialized to the client, so hydration uses exactly the value the server
    // rendered with — structural (Suspense) parity is guaranteed. Fail-closed:
    // default off ⇒ every <DeferredHydration> is an inert pass-through.
    <DeferredHydrationProvider enabled={deferHydration}>
      {layoutTree}
    </DeferredHydrationProvider>
  );
}

/**
 * Derive header/footer suppression flags from any matched child route that
 * exposes them via its loader data. Routes that render their own chrome
 * (embedded Builder Symbol, custom header/footer React component, etc.)
 * can opt out of the shared layout chrome by returning `hideLayoutHeader`
 * or `hideLayoutFooter` from their loader.
 */
function useLayoutChromeFlags() {
  const matches = useMatches();
  return useMemo(() => {
    let hideLayoutHeader = false;
    let hideLayoutFooter = false;
    for (const match of matches) {
      const matchData = (match as { data?: unknown }).data as
        | Record<string, unknown>
        | undefined;
      if (matchData?.hideLayoutHeader === true) hideLayoutHeader = true;
      if (matchData?.hideLayoutFooter === true) hideLayoutFooter = true;
    }
    return { hideLayoutHeader, hideLayoutFooter };
  }, [matches]);
}

const useSlot = (
  contentKey: keyof AppHandle,
): (() => React.JSX.Element | null) => {
  const matches = useMatches();
  const match = useMemo(
    () =>
      matches
        .slice()
        .reverse()
        .find(match => (match as any).handle?.[contentKey]),
    [matches, contentKey],
  );
  return useCallback(() => {
    if (!match) return null;
    return (match as any).handle?.[contentKey]?.(match.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.pathname]);
};
export const useRouteHandle = (): AppHandle | undefined => {
  const matches = useMatches();
  return useMemo(
    () =>
      (matches as any)
        .slice()
        .reverse()
        .find((match: { handle: any }) => match.handle)?.handle,
    [matches],
  );
};
